import { Box, Button, Grid, Tab, Theme, Typography, Tabs, Chip, FormControlLabel, Switch } from "@material-ui/core";
import { makeStyles } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import { observer } from "mobx-react";
import { AppraisalReviewSummaryData } from "./appraisalReviewSummaryData";
import { AppraisalReviewStore } from "../stores/appraisalReviewStore";
import { fieldTypes } from "./appraisalReviewConstants";
import { ConfirmationButton, StandardDialog, Tooltip } from "@roc/ui";
import { AppraisalAutomatedReview } from "./appraisalAutomatedReview";
import { AppraisalReviewAddItemToRevisionList } from "./appraisalReviewAddItemToRevisionList";
import { useEffect, useState } from "react";
import AppraisalReviewPhotos from "./appraisalReviewPhotos/appraisalReviewPhotosLinks";
import { ManualOrAutomatedChip } from "./manualOrAutomatedChip";
import { useUserStore } from "@roc/feature-app-core";
import SaveButtonWithPermissions from "libs/ui/src/lib/components/shared/save-button-with-permissions";
import { MANUAL_EDIT_APPRAISAL_REVIEW, OCR_FAILED_STATUS } from "./constants";
import { InternalRevisionTextBoxButton } from "./internalRevisionTextBoxButton";
import { AppraisalReviewRevisionList } from "./appraisalReviewRevisionList";
import { CloudDownload, Visibility, Publish } from "@material-ui/icons";
import { Document } from "@roc/feature-types";
import { AppraisalReportForm, PreviewDocument, useDocumentStore, WithDocumentActions } from "@roc/feature-documents";

export type SummaryData = {
  mapping: string,
  mappingToSave: string,
  title: string,
  value: any,
  options?: any,
  type: fieldTypes
  disabled: boolean
}

const useStyles = makeStyles((theme: Theme) => ({
  confidenceBanner: {
    marginBottom: '16px',
  },
  button: {
    color: '#2199E0',
    borderColor: '#2199E0',
    backgroundColor: '#E2F6FF',
  },
  toogleButton: {
    '&[disabled]': {
      color: '#bbb',
      '& svg *': {
        fill: '#bbb',
      },
    },
  },
}));

const CancelButton = ({ onClick, className }) => (
  <Button
    onClick={onClick}
    className={className}
    variant="outlined"
    color="primary"
    fullWidth
  >
    CANCEL
  </Button>
)

const SaveButton = ({ onClick }) => (
  <Button
    onClick={onClick}
    variant="outlined"
    color="primary"
    fullWidth
  >
    SAVE
  </Button>
);



export const EditableToogleButton = ({ onClick, checked, label }) => {
  const classes = useStyles();

  return (
    <FormControlLabel
      control={<Switch checked={checked} onChange={onClick} color="primary" />}
      label={label}
      labelPlacement="start"
    />
  );
}

interface Props {
  loanId: number;
  propertyId: number;
  store: AppraisalReviewStore;
  onClose?: () => void;
  hideTitle?: boolean;
  successCallback?: () => void;
  document?: Document;
}

export const AppraisalReviewReportForm = observer((props: Props) => {
  const classes = useStyles();
  const { store, propertyId, loanId, hideTitle, document } = props;
  const [selectedTab, setSelectedTab] = useState(0);
  const { userStore } = useUserStore();
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [showUploadReport, setShowUploadReport] = useState<boolean>(false);
  const { documentStore } = useDocumentStore();
  const { loanStore } = documentStore;

  useEffect(() => {
    if (loanStore.loanDetails?.selectedAmcId) {
      store.getAmcById(loanStore.loanDetails?.selectedAmcId);
    }
    store.getPropertyInformmation(propertyId, loanId);
    store.getAutomatedAppraisalReviewPhotos(propertyId, loanId);
    store.setSuccessCallback(props.successCallback);
    store.getAppraisalReviewItemNames();
  }, [store, loanId, propertyId]);

  const confidenceIssues = [
    store.extractionConfidence?.extractionSuccess === false &&
      'Low accuracy detected — please review the extracted values against the uploaded report.',
    store.extractionConfidence?.addressMatches === false &&
      'The property address in the report does not match.',
    store.extractionConfidence?.asIsValuePresent === false &&
      'The As-Is value was not found in the report.',
  ].filter(Boolean) as string[];

  const handleTabChange = (_, newValue) => {
    setSelectedTab(newValue);
  };

  const handleCloseButton = () => {
    window.parent.postMessage('close-modal', '*');
    props.onClose?.();
  };

  const handleConfirmationCompleteReviewAction = () => {
    store.approveReview(props.propertyId, props.loanId);
  };

  const saveAndClose = async () => {
    await store.saveDataAndClose(props.propertyId, props.loanId);
  }


  const onDownload = () => {
    documentStore.downloadDocument(document);
  }

  const handleClosePreview = () => {
    setShowPreview(false);
  }

  const handleCloseUploadReport = () => {
    setShowUploadReport(false);
  }


  return (
    <Box mt={3}>
      <StandardDialog
        open={showPreview}
        title={"Appraisal Report"}
        maxWidth="lg"
        handleClose={handleClosePreview}
        dialogContent={<PreviewDocument document={document} />}
      />
      <StandardDialog
        open={showUploadReport}
        title="Upload Appraisal Report"
        maxWidth="sm"
        handleClose={handleCloseUploadReport}
        dialogContent={
          <div style={{ marginTop: '-40px' }}>
            <AppraisalReportForm document={document} handleClose={handleCloseUploadReport} />
          </div>
        }
      />
      {
        hideTitle ? null :
          <Box mb={2} display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
            <Box display={'flex'} alignItems='center'>
              <Typography variant="h4">Appraisal Review Report</Typography>
              {documentStore.globalStore.isInternalPortal && (
                <>
                  <ManualOrAutomatedChip status={store.manualAutomatedStatus} />
                  {userStore.canEditAppraisalReportConfig() && (
                    <EditableToogleButton
                      checked={store.editEnabled}
                      label="Editable"
                      onClick={() => { store.updateReportEditableFlag(loanId, propertyId) }}
                    />
                  )}
                </>
              )}
            </Box>
            <Box display="flex" alignItems='center' justifyItems={'flex-end'}>
              {documentStore.isAppraisalDocumentUploadAllowed(document) && (
                <WithDocumentActions
                  component={Button}
                  document={document}
                  variant="contained"
                  color="primary"
                  endIcon={<Publish />}
                  size="small"
                  onClick={() => setShowUploadReport(true)}
                  style={{
                    padding: '16px 24px',
                  }}
                >
                  Upload Report
                </WithDocumentActions>
              )}
              {document?.canDownloadFile && (
                <>
                  <Button
                    variant="contained"
                    color="primary"
                    endIcon={<Visibility />}
                    size="small"
                    onClick={() => setShowPreview(true)}
                    style={{
                      padding: '16px 24px',
                      marginLeft: '8px',
                    }}
                  >
                    View Report
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    endIcon={<CloudDownload />}
                    size="small"
                    onClick={onDownload}
                    style={{
                      padding: '16px 24px',
                      marginLeft: '8px',
                    }}
                  >
                    Download Report
                  </Button>
                </>
              )}
            </Box>
          </Box>
      }
      {confidenceIssues.length > 0 && (
        <Alert severity="error" className={classes.confidenceBanner}>
          {confidenceIssues.length === 1 ? (
            confidenceIssues[0]
          ) : (
            <>
              Please review before continuing:
              <ul style={{ margin: '4px 0 0', paddingLeft: '20px' }}>
                {confidenceIssues.map(issue => (
                  <li key={issue}>{issue}</li>
                ))}
              </ul>
            </>
          )}
        </Alert>
      )}
      {confidenceIssues.length === 0 && store.extractionConfidence?.extractionSuccess === true && (
        <Alert severity="success" className={classes.confidenceBanner}>
          Document ready — the uploaded file has been processed
        </Alert>
      )}
      {store.amcCompany && <Box display={'flex'} alignItems={'center'}>
        <Typography style={{ padding: '10px 5px 10px 0' }}>Selected AMC</Typography>
        <Chip label={store.amcCompany.name} />
      </Box>}
      <Box>
        <AppraisalReviewSummaryData store={store} />
      </Box>
      <Box mt={2} mb={3}>
        <Grid container spacing={2} xs={12} justifyContent="flex-end">
          <Grid item xs={12} sm={6} md={2}>
            <CancelButton onClick={handleCloseButton} className={classes.button} />
          </Grid>
          {documentStore.globalStore.isInternalPortal && (
            <Grid item xs={12} sm={6} md={2}>
              <WithDocumentActions
                component={SaveButtonWithPermissions}
                document={document}
                onClick={() => store.saveDataAndClose(props.propertyId, props.loanId)}
                loanId={props.loanId}
                label="SAVE"
                variant="outlined"
                color="primary"
                fullWidth
                disabled={store.manualAutomatedStatus === MANUAL_EDIT_APPRAISAL_REVIEW && store.loanPropertyValuationStatus !== OCR_FAILED_STATUS}
              />
            </Grid>
          )}
        </Grid>
      </Box>
      <Box mb={2}>
        <Tabs value={selectedTab} onChange={handleTabChange}>
          <Tab label="Appraisal Review" />
          <Tab label="Photos Review" />
        </Tabs>
      </Box>
      {selectedTab === 0 && (<>
        <Box>
          <AppraisalAutomatedReview propertyId={props.propertyId} loanId={props.loanId} store={store} />
        </Box>
        {<Box>
          <AppraisalReviewRevisionList propertyId={props.propertyId} loanId={props.loanId} store={store} />
        </Box>}
        <Box mt={2}>
          <Grid container spacing={2} xs={12} justifyContent="flex-end">
            {documentStore.globalStore.isInternalPortal && (
              <Grid container spacing={2} justifyContent="flex-end" style={{ paddingBottom: '10px' }}>
                {/* {store.taskFrameworkEnabled &&
                (<Grid item xs={12} sm={4} md={3}>
                  <AppraisalReviewAddItemToRevisionList propertyId={props.propertyId} loanId={props.loanId} store={store} />
                </Grid>
                )} */}
                <Grid item xs={12} sm={4} md={3}>
                  <WithDocumentActions
                    component={ConfirmationButton}
                    document={document}
                    variant="contained"
                    onClick={handleConfirmationCompleteReviewAction}
                    color="primary"
                    testId="confirmReview"
                    confirmDialogProps={{
                      title: 'Complete Review',
                      body: 'Are you sure you want to mark this review as completed?',
                    }}
                    fullWidth
                    disabled={store.manualAutomatedStatus === MANUAL_EDIT_APPRAISAL_REVIEW && store.loanPropertyValuationStatus !== OCR_FAILED_STATUS}
                  >
                    REVIEW COMPLETE
                  </WithDocumentActions>
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  <InternalRevisionTextBoxButton document={document} store={store} propertyId={propertyId} loanId={loanId} />
                </Grid>
              </Grid>
            )}
            {/* divider */}
            <Grid container spacing={2} justifyContent="flex-end">
              <Grid item xs={12} sm={3} md={2}>
                <CancelButton onClick={handleCloseButton} className={classes.button} />
              </Grid>
              {documentStore.globalStore.isInternalPortal && (
                <Grid item xs={12} sm={3} md={2}>
                  <WithDocumentActions
                    component={SaveButtonWithPermissions}
                    document={document}
                    onClick={() => store.saveDataAndClose(props.propertyId, props.loanId)}
                    loanId={props.loanId}
                    label="SAVE"
                    variant="outlined"
                    color="primary"
                    fullWidth
                    disabled={store.manualAutomatedStatus === MANUAL_EDIT_APPRAISAL_REVIEW && store.loanPropertyValuationStatus !== OCR_FAILED_STATUS}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
        </Box></>)}

      {selectedTab == 1 && (
        <Box>
          <AppraisalReviewPhotos propertyId={props.propertyId} loanId={props.loanId} store={store} />
        </Box>
      )}
    </Box>
  )
});
